import { useLayoutEffect, useRef, useState } from 'react'
import './BottomNav.css'
import { tabs, type Tab } from './navTabs'
import {
  RISE_SPACE,
  HERO_HALF_WIDTH,
  HERO_RAMP,
  SIDE_HALF_WIDTH,
  SIDE_RAMP,
  TOTAL_HEIGHT,
  NOTCH_ANIM_MS,
  buildBarPath,
  computeNotchFrame,
  type NotchParams,
} from './bottomNavNotch'

export type { Tab }

interface BottomNavProps {
  activeTab: Tab
  onTabChange: (tab: Tab) => void
  /** Pending incoming rivalry challenges — surfaced as a badge dot on the Home (Profile) tab,
   * which leads to the Rivals list. Ambient/secondary: the list itself always shows these too. */
  pendingRivalRequestsCount?: number
}

function BottomNav({ activeTab, onTabChange, pendingRivalRequestsCount = 0 }: BottomNavProps) {
  const activeIndex = tabs.findIndex((tab) => tab.id === activeTab)
  const navRef = useRef<HTMLElement>(null)
  const [width, setWidth] = useState(0)
  // The bar's actual rendered box height (56 + rise-space +
  // env(safe-area-inset-bottom)) — measured rather than assumed, so the
  // notch path's flat bottom edge always reaches the box's true bottom.
  // Read via getBoundingClientRect (border-box) rather than
  // ResizeObserver's contentRect, which excludes the padding that carries
  // the safe-area inset.
  const [height, setHeight] = useState(TOTAL_HEIGHT)
  const [d, setD] = useState('')
  const currentParamsRef = useRef<NotchParams | null>(null)
  const prevKeyRef = useRef<string | null>(null)
  const rafRef = useRef<number | null>(null)

  useLayoutEffect(() => {
    const el = navRef.current
    if (!el) return
    const measure = () => {
      const rect = el.getBoundingClientRect()
      setWidth(rect.width)
      setHeight(rect.height)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const isHero = tabs[activeIndex]?.id === 'predict'
  const centerX = (width / tabs.length) * (activeIndex + 0.5)

  useLayoutEffect(() => {
    if (width <= 0) return
    const target: NotchParams = {
      centerX,
      halfWidth: isHero ? HERO_HALF_WIDTH : SIDE_HALF_WIDTH,
      ramp: isHero ? HERO_RAMP : SIDE_RAMP,
    }
    const key = `${activeIndex}-${isHero}`

    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }

    // Only animate on an actual tab change — a resize/layout-driven width
    // change (or the initial mount) should snap straight to the target.
    const tabChanged = prevKeyRef.current !== null && prevKeyRef.current !== key
    prevKeyRef.current = key

    if (!tabChanged || !currentParamsRef.current) {
      currentParamsRef.current = target
      setD(buildBarPath(width, target.centerX, target.halfWidth, target.ramp, height))
      return
    }

    const from = currentParamsRef.current
    const start = performance.now()

    const tick = (now: number) => {
      const elapsed = now - start
      const cur = computeNotchFrame(from, target, elapsed)
      currentParamsRef.current = cur
      setD(buildBarPath(width, cur.centerX, cur.halfWidth, cur.ramp, height))
      rafRef.current = elapsed < NOTCH_ANIM_MS ? requestAnimationFrame(tick) : null
    }
    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [activeIndex, isHero, width, height, centerX])

  return (
    <nav
      ref={navRef}
      className="bottom-nav"
      style={{ '--rise-space': `${RISE_SPACE}px` } as React.CSSProperties}
    >
      {d && (
        <>
          <div
            className="bottom-nav__bar"
            style={
              {
                clipPath: `path("${d}")`,
                WebkitClipPath: `path("${d}")`,
              } as React.CSSProperties
            }
            aria-hidden="true"
          ></div>
          <svg
            className="bottom-nav__edge"
            width="100%"
            height="100%"
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d={d}
              fill="none"
              stroke="var(--color-bottom-nav-edge)"
              strokeWidth="1"
            />
          </svg>
        </>
      )}
      {tabs.map((tab) => (
        <button
          key={tab.id}
          className={`bottom-nav__item ${
            tab.id === 'predict' ? 'bottom-nav__item--predict' : ''
          } ${activeTab === tab.id ? 'bottom-nav__item--active' : ''}`}
          onClick={() => onTabChange(tab.id)}
          aria-label={tab.label}
        >
          <span className="bottom-nav__icon-wrap">
            <tab.icon className="bottom-nav__icon" aria-hidden="true" />
            {tab.id === 'home' && pendingRivalRequestsCount > 0 && (
              <span className="bottom-nav__badge">{pendingRivalRequestsCount}</span>
            )}
          </span>
        </button>
      ))}
    </nav>
  )
}

export default BottomNav
